import { AxiosResponse } from 'axios';
import React from 'react';
import { IDataRequest, IDataResponse, IPaginationParams, IResultResponse, ISortField, ISortFieldResponse } from '@hooks/useRequest';
import { IFilter, useFilter } from '@hooks/useFilter';
import { IPagination, usePagination } from '@hooks/usePagination';
import { ISort, useSort } from '@hooks/useSort';
import { FormFields } from './useForm';

export type DataRow<T> = T & {
    selected?: boolean,
    focused?: boolean
}

export type DataState<TRow extends object, TFilter extends object = object> = {
    data?: DataRow<TRow>[],
    sortField?: ISortField,
    pagination?: IPaginationParams,
    filter?: FormFields<TFilter>,
}

export interface IData<TRow, TFilter extends object = object>{
    items: DataRow<TRow>[],
    itemsCount: number,
    isEmpty: boolean,

    addRow: (newRow: TRow) => void,
    insertRow: (index: number, newRow: TRow) => void,
    updateRow: (oldRow: TRow | undefined, newRow: TRow) => void,
    removeRow: (row: DataRow<TRow>) => void,

    focusedRow: TRow | undefined,
    getFocusedRow: () => DataRow<TRow> | undefined,
    setFocusedRow: (row: TRow) => void,
    updateFocusedRow: (newRow: TRow) => void,
    handleUpdateFocusedRow: (response: AxiosResponse<TRow>) => void,
    handleRowFocused: (row: TRow, onAfterFocused?: (anchorEl: HTMLElement) => void) => (e: React.MouseEvent<HTMLElement>) => void

    clear: VoidFunction,
    updateAll: (newData: TRow[]) => void
    handleDataResponse: (response: AxiosResponse<TRow[]>) => void
    updateDataResponse: (data: IDataResponse<TRow[]>) => void

    requestedData: IDataRequest<TFilter>,
    pagination: IPagination,
    sort: ISort,
    filter: IFilter<TFilter>,
    
    handleResultResponse: (response: AxiosResponse<IDataResponse<TRow[]>>) => void
    handleSortResponse: (response: AxiosResponse<IResultResponse<TRow[]> & ISortFieldResponse>) => void
}

export function useData<TRow extends object, TFilter extends object = object> (initialState?: DataState<TRow, TFilter>) : IData<TRow, TFilter> {
    const [rows, setRows] = React.useState<DataRow<TRow>[]>([]);

    const filter = useFilter<TFilter>(initialState?.filter || {} as FormFields<TFilter>);
    const pagination = usePagination(initialState?.pagination);
    const sort = useSort(initialState?.sortField);

    const [requestedData, setRequestedData] = React.useState<IDataRequest<TFilter>>({
        filter: filter.getInputValues(),
        pagination: pagination.requestedPagination,
        sortField: sort.requestedSortField
    });

    //ROW
    const addRow = React.useCallback((newRow: TRow) => {
        setRows(s => [...s, newRow]);
    }, []);

    const insertRow = React.useCallback((index: number, newRow: TRow) => {
        setRows(s => {
            const _s = [...s];
            _s.splice(index, 0);
            return _s;
        });
    }, []);

    const updateRow = React.useCallback((oldRow: TRow | undefined, newRow: TRow) : void => {
        if (oldRow !== undefined) {
            const sRows = [...rows];
            const index = sRows.findIndex(x => x === oldRow);

            if (index !== -1) {
                sRows[index] = newRow;
                setRows(sRows);
            }
        }
    }, [rows]);

    const removeRow = React.useCallback((row: DataRow<TRow>) => {
        setRows(s => {
            const _s = [...s];
            const index = _s.findIndex(x => x === row);

            _s.splice(index, 1);
            return _s;
        });
    }, []);

    //ROW FOCUSING
    const getFocusedRow = React.useCallback(() => {
        return rows.find(x => x.focused === true);
    }, [rows]);

    const setFocusedRow = React.useCallback((row: TRow) : void => {
        const sItems = [...rows];
        const index = sItems.findIndex(x => x === row);

        if (index !== -1) {
            sItems.forEach(x => {x.focused = false});
            sItems[index].focused = true;
            setRows(sItems);
        }
    }, [rows]);

    const updateFocusedRow = React.useCallback((newRow: TRow) => {
        updateRow(getFocusedRow(), newRow);
    }, [getFocusedRow, updateRow]);

    const handleUpdateFocusedRow = React.useCallback((response: AxiosResponse<TRow>) => {
        updateFocusedRow(response.data);
    }, [updateFocusedRow]);

    const handleRowFocused = React.useCallback((row: TRow, onAfterFocused?: (anchorEl: HTMLElement) => void) => (e: React.MouseEvent<HTMLElement>) => {
        setFocusedRow(row);
        
        if (onAfterFocused !== undefined) {
            onAfterFocused(e.currentTarget);
        }
    }, [setFocusedRow]);

    //COLLECTION
    const clear = React.useCallback(() => {
        setRows([]);
    }, []);

    const updateAll = React.useCallback((newData: TRow[]) => {
        setRows(newData);
    }, []);

    const updateDataResponse = React.useCallback((data: IDataResponse<TRow[]>) => {
        updateAll(data.result);
        pagination.updatePagination(data.pagination);
        sort.updateSortField(data.sortField)
    }, [pagination, sort, updateAll])

    const handleDataResponse = React.useCallback((response: AxiosResponse<TRow[]>) => {
        updateAll(response.data);
    }, [updateAll]);

    const handleResultResponse = React.useCallback((response: AxiosResponse<IDataResponse<TRow[]>>) => {
        updateDataResponse(response.data);
    }, [updateDataResponse]);

    const handleSortResponse = React.useCallback((response: AxiosResponse<IResultResponse<TRow[]> & ISortFieldResponse>) => {
        updateAll(response.data.result);
        sort.updateSortField(response.data.sortField)
    }, [sort, updateAll]);

    React.useEffect(() => {
        setRequestedData(s => ({
            ...s,
            filter: filter.requestedFilter,
            pagination: pagination.requestedPagination,
            sortField: sort.requestedSortField
        }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.requestedPagination]);

    React.useEffect(() => {
        pagination.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort.requestedSortField, filter.requestedFilter]);

    return React.useMemo(() => ({
        items: rows,
        itemsCount: rows.length,
        isEmpty: rows.length === 0,

        filter, pagination, sort,

        addRow, insertRow, updateRow, removeRow,

        focusedRow: getFocusedRow(),
        getFocusedRow, setFocusedRow, updateFocusedRow, handleRowFocused,

        clear, updateAll, 
        
        handleDataResponse, handleResultResponse, handleSortResponse, handleUpdateFocusedRow,
        updateDataResponse,

        requestedData
    }), [addRow, removeRow, clear, filter, updateDataResponse, getFocusedRow, handleDataResponse, handleResultResponse, handleRowFocused, handleSortResponse, handleUpdateFocusedRow, insertRow, pagination, requestedData, rows, setFocusedRow, sort, updateAll, updateFocusedRow, updateRow]);
}